import React, { useState, useEffect } from "react";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { RefreshCw, Brain, Globe, TrendingUp, Clock, Trash2, CheckCircle2, CalendarClock, FileText, Youtube, HelpCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "../../lib/backend";
import ModelRegistryCard from "./ModelRegistryCard";

const API = BACKEND_URL + "/api";

const fmtTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never";

export default function ActionsTab({
  dm, triggerLoading, handleTrigger,
  summaryModel, availableModels, savingModel, saveSummaryModel,
  authHeader,
}) {
  const [refreshSettings, setRefreshSettings] = useState({ enabled: true, interval_hours: 12, last_refreshed_at: null });
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [savingRefresh, setSavingRefresh] = useState(false);

  // Load refresh settings once on mount. authHeader is a stable function from context and API is
  // a module-level constant — neither changes identity between renders. Suppressing exhaustive-deps
  // is intentional to avoid infinite refetch loops.
   
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingRefresh(true);
        const res = await axios.get(`${API}/admin/refresh-settings`, { headers: authHeader() });
        setRefreshSettings(res.data);
      } catch (err) {
        console.error("[ActionsTab] Failed to load refresh settings:", err?.response?.status ?? err?.message);
      }
      finally { setLoadingRefresh(false); }
    };
    load();
  }, []);

  const saveRefreshSettings = async (patch) => {
    const updated = { ...refreshSettings, ...patch };
    setRefreshSettings(updated);
    try {
      setSavingRefresh(true);
      await axios.put(`${API}/admin/refresh-settings`, {
        enabled: updated.enabled,
        interval_hours: updated.interval_hours,
      }, { headers: authHeader() });
      toast.success("Auto-refresh settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSavingRefresh(false); }
  };

  const [cleanupResult, setCleanupResult] = useState(null);
  const [runningCleanup, setRunningCleanup] = useState(false);

  const runCleanup = async () => {
    setRunningCleanup(true);
    setCleanupResult(null);
    try {
      const res = await axios.post(`${API}/admin/cleanup-invalid-articles`, {}, { headers: authHeader() });
      setCleanupResult(res.data);
      toast.success(`Cleanup done: ${res.data.deleted} invalid articles removed`);
    } catch {
      toast.error("Cleanup failed");
    } finally {
      setRunningCleanup(false);
    }
  };

  const [archiveCleanupResult, setArchiveCleanupResult] = useState(null);
  const [runningArchiveCleanup, setRunningArchiveCleanup] = useState(false);

  const runArchiveCleanup = async () => {
    setRunningArchiveCleanup(true);
    setArchiveCleanupResult(null);
    try {
      const res = await axios.post(`${API}/admin/cleanup-archive-dates`, {}, { headers: authHeader() });
      setArchiveCleanupResult(res.data);
      toast.success(`Date cleanup done: ${res.data.suspicious_deleted} removed, ${res.data.archive_fields_rebuilt} rebuilt`);
    } catch {
      toast.error("Archive date cleanup failed");
    } finally {
      setRunningArchiveCleanup(false);
    }
  };

  const [reEnrichResult, setReEnrichResult] = useState(null);
  const [runningReEnrich, setRunningReEnrich] = useState(false);

  const runResearchReEnrich = async () => {
    if (!window.confirm("Re-summarise ALL reports with the latest prompt? This will rewrite existing abstracts and may consume Firecrawl credits + GPT-4o-mini tokens (~$0.30 for 100 reports). Continue?")) return;
    setRunningReEnrich(true);
    setReEnrichResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/reports/enrich`,
        null,
        {
          headers: authHeader(),
          params: { max_reports: 200, skip_hours: 0, use_firecrawl: true, force_regenerate: true },
        },
      );
      setReEnrichResult({ ok: true, message: res.data?.message || "Enrichment started" });
      toast.success("Re-enrich started — check Reports page in ~5 minutes");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Re-enrich failed";
      setReEnrichResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setRunningReEnrich(false);
    }
  };

  const [dateRepairResult, setDateRepairResult] = useState(null);
  const [runningDateRepair, setRunningDateRepair] = useState(false);

  const runDateRepair = async () => {
    setRunningDateRepair(true);
    setDateRepairResult(null);
    try {
      const res = await axios.post(`${API}/admin/maintenance/repair-dates`, null, {
        headers: authHeader(), params: { days: 30, max_checks: 120 }, timeout: 240000,
      });
      setDateRepairResult(res.data);
      const r = res.data;
      toast.success(`Date repair: ${r.corrected} corrected · ${r.estimated_by_ai} AI-estimated · ${r.confirmed_ok} confirmed · ${r.undatable} undatable · ${r.remaining_unchecked} remaining · reports: ${r.reports_corrected ?? 0} fixed / ${r.reports_estimated_by_ai ?? 0} AI / ${r.reports_undatable ?? 0} undated / ${r.reports_remaining ?? 0} remaining`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Date repair failed");
    } finally {
      setRunningDateRepair(false);
    }
  };

  const [faqBackfillResult, setFaqBackfillResult] = useState(null);
  const [runningFaqBackfill, setRunningFaqBackfill] = useState(false);

  const runFaqBackfill = async () => {
    setRunningFaqBackfill(true);
    setFaqBackfillResult(null);
    try {
      const res = await axios.post(`${API}/admin/briefs/backfill-faqs`, null, {
        headers: authHeader(), timeout: 240000,
      });
      setFaqBackfillResult(res.data);
      toast.success(`Brief FAQs: ${res.data.generated} generated · ${res.data.failed} failed · ${res.data.targets} targets`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "FAQ backfill failed");
    } finally {
      setRunningFaqBackfill(false);
    }
  };

  // YouTube ingest — accept a textarea of URLs (videos or channels). Channel URLs
  // listed with prefix `channel:` are sent to channel_urls (pulls top N recent
  // videos with transcript). Everything else goes to urls[].
  const [ytText, setYtText] = useState("");
  const [ytMaxPerChannel, setYtMaxPerChannel] = useState(3);
  const [runningYt, setRunningYt] = useState(false);
  const [ytResult, setYtResult] = useState(null);

  const runYoutubeIngest = async () => {
    const lines = ytText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) {
      toast.error("Add at least one YouTube URL (one per line)");
      return;
    }
    const channel_urls = [];
    const urls = [];
    lines.forEach((line) => {
      const v = line.replace(/^channel:\s*/i, "").trim();
      if (line.toLowerCase().startsWith("channel:") || /youtube\.com\/(@|c\/|channel\/|user\/)/i.test(v)) {
        channel_urls.push(v);
      } else {
        urls.push(v);
      }
    });
    setRunningYt(true);
    setYtResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/youtube/ingest`,
        { urls, channel_urls, max_per_channel: ytMaxPerChannel },
        { headers: authHeader(), timeout: 180000 },
      );
      setYtResult({ ok: true, ...res.data });
      const { inserted = 0, skipped_existing = 0, fetched = 0 } = res.data || {};
      toast.success(`YouTube ingest: ${inserted} new article${inserted === 1 ? "" : "s"} (fetched ${fetched}, skipped ${skipped_existing})`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "YouTube ingest failed";
      setYtResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setRunningYt(false);
    }
  };

  return (
    <TabsContent value="actions">
      {/* Auto-Refresh Settings */}
      <div className={`rounded-2xl mb-8 overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
        <div className={`px-6 py-4 flex items-center justify-between border-b ${dm ? "border-white/5 bg-[#161d1e]" : "border-black/5 bg-[#fafbfb]"}`}>
          <div className="flex items-center gap-3">
            <Clock className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
            <div>
              <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Auto RSS Refresh</p>
              <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                {loadingRefresh ? "Loading…" : refreshSettings.enabled
                  ? `Runs every ${refreshSettings.interval_hours}h · Last: ${fmtTime(refreshSettings.last_refreshed_at)}`
                  : `Disabled · Last: ${fmtTime(refreshSettings.last_refreshed_at)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Interval selector */}
            <div className={`flex items-center gap-1 rounded-xl p-1 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"} ${!refreshSettings.enabled ? "opacity-40 pointer-events-none" : ""}`}>
              {[12, 24, 48].map(h => (
                <button
                  key={h}
                  data-testid={`refresh-interval-${h}h`}
                  onClick={() => saveRefreshSettings({ interval_hours: h })}
                  className={`px-3 py-1 rounded-lg category-tag text-xs transition-all ${refreshSettings.interval_hours === h
                    ? (dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white")
                    : (dm ? "text-slate-400 hover:text-slate-200" : "text-[#6b7a7d] hover:text-[#191c1d]")
                  }`}
                >
                  {h}h
                </button>
              ))}
            </div>
            {/* ON/OFF toggle */}
            <div className="flex items-center gap-2">
              <span className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                {refreshSettings.enabled ? "ON" : "OFF"}
              </span>
              <Switch
                checked={refreshSettings.enabled}
                onCheckedChange={v => saveRefreshSettings({ enabled: v })}
                disabled={savingRefresh}
                data-testid="auto-refresh-toggle"
                className="data-[state=checked]:bg-[#006874]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[
          { key: "refresh", icon: RefreshCw, title: "RSS Refresh", desc: "Scrape all configured RSS feeds and generate AI summaries for new articles.", label: "Trigger Refresh", cost: "~20 AI calls" },
          { key: "perplexity", icon: Brain, title: "Perplexity AI Search", desc: "Run targeted AI search across the web for travel tech news not covered by RSS feeds.", label: "Run Perplexity Search", cost: "~5 Perplexity calls" },
          { key: "discovery", icon: Globe, title: "Massive Discovery", desc: "Broad Perplexity discovery sweep across multiple strategies and sources.", label: "Run Massive Discovery", cost: "~15 Perplexity calls" },
          { key: "historical", icon: TrendingUp, title: "Historical Discovery", desc: "Collect and archive travel tech news from the past 3 months using 15 specialized search strategies.", label: "Start Historical Discovery", cost: "~50 Perplexity calls · Long-running" },
        ].map(({ key, icon: Icon, title, desc, label, cost }) => (
          <div key={key} className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
              <Icon className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
            </div>
            <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{title}</h3>
            <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{desc}</p>
            <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{cost}</div>
            <button
              onClick={() => handleTrigger(key)}
              disabled={!!triggerLoading[key]}
              className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
            >
              {triggerLoading[key] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
              {triggerLoading[key] ? "Running…" : label}
            </button>
          </div>
        ))}

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-red-400/10" : "bg-red-50"}`}>
            <Trash2 className={`w-5 h-5 ${dm ? "text-red-400" : "text-red-500"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Cleanup Invalid Articles</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Remove articles with no working URL, placeholder titles (&ldquo;Key Takeaways:&rdquo;, &ldquo;Summary:&rdquo;, etc.) or AI-generated filler summaries (&ldquo;Article about…&rdquo;).</p>
          {cleanupResult && (
            <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {cleanupResult.deleted === 0
                ? `No invalid articles found — DB is clean (${cleanupResult.remaining} articles)`
                : `Removed ${cleanupResult.deleted} · ${cleanupResult.remaining} articles remaining`}
            </div>
          )}
          <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>No API calls · Instant</div>
          <button
            onClick={runCleanup}
            disabled={runningCleanup}
            data-testid="cleanup-invalid-articles-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-red-400/15 text-red-400 hover:bg-red-400/25" : "bg-red-50 text-red-600 hover:bg-red-100"}`}
          >
            {runningCleanup ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Cleaning…</> : <><Trash2 className="w-3.5 h-3.5" />Run Cleanup</>}
          </button>
        </div>

        {/* Fix Archive Dates */}
        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-amber-400/10" : "bg-amber-50"}`}>
            <CalendarClock className={`w-5 h-5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Fix Archive Dates</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Removes AI-discovered articles whose publication date was set to the scraping time (date parsing failed). Also rebuilds <code className="text-xs">timelinePosition</code> and archive fields for all articles so they match their real <code className="text-xs">publishedAt</code>.
          </p>
          {archiveCleanupResult && (
            <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {archiveCleanupResult.suspicious_deleted === 0
                ? `No suspect dates found · Rebuilt ${archiveCleanupResult.archive_fields_rebuilt} archive records`
                : `Removed ${archiveCleanupResult.suspicious_deleted} bad-date articles · Rebuilt ${archiveCleanupResult.archive_fields_rebuilt} records`}
            </div>
          )}
          <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>No API calls · Recommended after discovery runs</div>
          <button
            onClick={runArchiveCleanup}
            disabled={runningArchiveCleanup}
            data-testid="cleanup-archive-dates-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-amber-400/15 text-amber-400 hover:bg-amber-400/25" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
          >
            {runningArchiveCleanup ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Fixing…</> : <><CalendarClock className="w-3.5 h-3.5" />Fix Dates</>}
          </button>
        </div>

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-amber-400/10" : "bg-amber-50"}`}>
            <CalendarClock className={`w-5 h-5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Repair Publish Dates</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Finds articles whose date was stamped at scrape time (old content shown as &ldquo;today&rdquo;), re-reads the real date from the live page, falls back to AI inference from the text, and sinks anything still undatable to the end of the feed. Re-run until &ldquo;remaining&rdquo; hits 0.
          </p>
          {dateRepairResult && (
            <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {`${dateRepairResult.corrected} corrected · ${dateRepairResult.estimated_by_ai ?? 0} AI-estimated · ${dateRepairResult.confirmed_ok} confirmed · ${dateRepairResult.undatable ?? 0} undatable — ${dateRepairResult.remaining_unchecked ?? 0} remaining to check`}
            </div>
          )}
          <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Free page fetches · ~1-2 min</div>
          <button
            onClick={runDateRepair}
            disabled={runningDateRepair}
            data-testid="repair-dates-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-amber-400/15 text-amber-400 hover:bg-amber-400/25" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
          >
            {runningDateRepair ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Repairing…</> : <><CalendarClock className="w-3.5 h-3.5" />Repair Dates</>}
          </button>
        </div>

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-teal-400/10" : "bg-teal-50"}`}>
            <HelpCircle className={`w-5 h-5 ${dm ? "text-teal-400" : "text-teal-600"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Brief FAQs Backfill</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Generates the &quot;Key questions this week&quot; block (3 Q&amp;As + FAQPage JSON-LD) for the current brief and every published archive brief missing it. New briefs get FAQs automatically.
          </p>
          {faqBackfillResult && (
            <div className={`flex items-center gap-2 text-xs mb-3 ${dm ? "text-teal-400" : "text-teal-700"}`} data-testid="faq-backfill-result">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {`${faqBackfillResult.generated} generated · ${faqBackfillResult.failed} failed · ${faqBackfillResult.targets} targets`}
            </div>
          )}
          <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>~5s per brief · LLM</div>
          <button
            onClick={runFaqBackfill}
            disabled={runningFaqBackfill}
            data-testid="brief-faq-backfill-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-teal-400/15 text-teal-400 hover:bg-teal-400/25" : "bg-teal-50 text-teal-700 hover:bg-teal-100"}`}
          >
            {runningFaqBackfill ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Generating…</> : <><HelpCircle className="w-3.5 h-3.5" />Generate Brief FAQs</>}
          </button>
        </div>

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-emerald-400/10" : "bg-emerald-50"}`}>
            <FileText className={`w-5 h-5 ${dm ? "text-emerald-400" : "text-emerald-600"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Research Re-enrich All</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Rewrites every report abstract using the latest enrichment prompt (multi-tier fetch: direct → web-search → Firecrawl). Use after a prompt update to roll out across the back-catalogue.
          </p>
          {reEnrichResult && (
            <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${reEnrichResult.ok
              ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700")
              : (dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-700")
            }`}>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{reEnrichResult.message}</span>
            </div>
          )}
          <div className={`category-tag mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>~3s per report · ~$0.30 / 100 reports · Firecrawl credits</div>
          <button
            onClick={runResearchReEnrich}
            disabled={runningReEnrich}
            data-testid="reenrich-reports-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-emerald-400/15 text-emerald-400 hover:bg-emerald-400/25" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
          >
            {runningReEnrich ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Re-enriching…</> : <><FileText className="w-3.5 h-3.5" />Re-enrich All</>}
          </button>
        </div>

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-red-400/10" : "bg-red-50"}`}>
            <Youtube className={`w-5 h-5 ${dm ? "text-red-400" : "text-red-600"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>YouTube Ingest</h3>
          <p className={`text-sm leading-relaxed mb-3 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Pull metadata + full transcript from YouTube videos or channels via yt-dlp. One URL per line. Channels (URLs with @, /c/, /channel/, /user/) pull their N most recent videos.
          </p>
          <textarea
            value={ytText}
            onChange={(e) => setYtText(e.target.value)}
            placeholder={"https://www.youtube.com/@SkiftTravel\nhttps://www.youtube.com/watch?v=…"}
            disabled={runningYt}
            data-testid="youtube-ingest-textarea"
            rows={3}
            className={`w-full px-3 py-2 mb-3 text-xs font-mono rounded-lg border resize-none ${dm ? "bg-[#0f1416] border-white/10 text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] border-black/5 text-[#191c1d] placeholder-[#bac9cc]"}`}
          />
          <div className="flex items-center gap-2 mb-3">
            <label className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Per channel:</label>
            <input
              type="number"
              value={ytMaxPerChannel}
              onChange={(e) => setYtMaxPerChannel(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              disabled={runningYt}
              data-testid="youtube-max-per-channel"
              min="1"
              max="20"
              className={`w-16 px-2 py-1 text-xs rounded-md border ${dm ? "bg-[#0f1416] border-white/10 text-slate-200" : "bg-white border-black/10 text-[#191c1d]"}`}
            />
            <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>recent videos</span>
          </div>
          {ytResult && (
            <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs ${ytResult.ok
              ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700")
              : (dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-700")
            }`}>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{ytResult.ok ? `+${ytResult.inserted || 0} new · ${ytResult.fetched || 0} fetched · ${ytResult.skipped_existing || 0} dupes` : (ytResult.message || "Failed")}</span>
            </div>
          )}
          <button
            onClick={runYoutubeIngest}
            disabled={runningYt || !ytText.trim()}
            data-testid="youtube-ingest-btn"
            className={`w-full py-2.5 rounded-full category-tag transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${dm ? "bg-red-400/15 text-red-400 hover:bg-red-400/25" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
          >
            {runningYt ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Ingesting…</> : <><Youtube className="w-3.5 h-3.5" />Ingest</>}
          </button>
        </div>

        <div className={`rounded-2xl p-6 flex flex-col ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
            <Brain className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          </div>
          <h3 className={`editorial-heading text-lg font-medium mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>AI Summary Model</h3>
          <p className={`text-sm leading-relaxed mb-4 flex-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Choose which AI model generates article summaries during scraping.</p>
          <select
            data-testid="summary-model-select"
            value={summaryModel}
            onChange={(e) => saveSummaryModel(e.target.value)}
            disabled={savingModel || availableModels.length === 0}
            className={`w-full px-3 py-2.5 rounded-xl text-sm font-mono border-0 outline-none appearance-none cursor-pointer transition-colors ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"} disabled:opacity-50`}
          >
            {availableModels.length === 0 && <option value={summaryModel}>{summaryModel}</option>}
            {availableModels.map(m => (
              <option key={m.id} value={m.id}>{m.label} — {m.note}</option>
            ))}
          </select>
          {savingModel && <p className={`mt-2 text-xs category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Saving…</p>}
        </div>
      </div>

      <ModelRegistryCard dm={dm} authHeader={authHeader} />
    </TabsContent>
  );
}
